import { CLASS_HIDDEN, CLASS_IS_ACTIVE } from '@theme/constants'
import { matchesSearch, matchesTag, tokenize } from '@theme/blog-search'

// CONSTANTS
const SELECTOR_ROOT = '[data-blog-filter-root]'
const SELECTOR_NAV = '[data-blog-filter-nav]'
const SELECTOR_TAB = '[data-blog-filter-tab]'
const SELECTOR_CARD = '[data-blog-filter-card]'
const SELECTOR_LOAD_MORE_WRAPPER = '[data-blog-filter-load-more-wrapper]'
const SELECTOR_LOAD_MORE = '[data-blog-filter-load-more]'
const SELECTOR_ANNOUNCE = '[data-blog-filter-announce]'
const SELECTOR_EMPTY = '[data-blog-filter-empty]'
const SELECTOR_TABS_LIST = '.m-blog-journal-header__tabs'
const SELECTOR_SEARCH_ACTIVATE = '[data-blog-filter-search-activate]'
const SELECTOR_SEARCH_FIELD = '[data-blog-filter-search-field]'
const SELECTOR_SEARCH_INPUT = '[data-blog-filter-search-input]'
const SELECTOR_SEARCH_CLOSE = '[data-blog-filter-search-close]'
const SELECTOR_SEARCH_CLEAR = '[data-blog-filter-search-clear]'

const DATA_INIT_FLAG = 'blogFilterInit'
const DATA_TAGS_ATTR = 'data-blog-filter-tags'
const DATA_BATCH_SIZE_ATTR = 'data-blog-filter-batch-size'
const DATA_TAB_ATTR = 'data-blog-filter-tab'
const DATA_SEARCH_TEXT_ATTR = 'data-blog-filter-search-text'

const ALL_TAG = ''
const DEFAULT_BATCH_SIZE = 6
const EVENT_DOM_CONTENT_LOADED = 'DOMContentLoaded'
const EVENT_SHOPIFY_SECTION_LOAD = 'shopify:section:load'
const KEY_ESCAPE = 'Escape'

// Wait for typing to pause before updating the live region, otherwise
// screen readers get a result count per keystroke.
const SEARCH_ANNOUNCE_DELAY_MS = 500

// A

/** Announce the current result count to screen readers.
 *
 * @param {Object} state - Filter state (see applyFilter).
 * @param {number} matchCount - Number of cards matching the filters.
 */
const announceResults = (state, matchCount) => {
  if (!state.announce) return

  const noun = matchCount === 1 ? 'post' : 'posts'
  const scope = state.activeLabel ? ` in ${state.activeLabel}` : ''
  const query = state.searchQuery ? ` for “${state.searchQuery}”` : ''
  state.announce.textContent = `Showing ${matchCount} ${noun}${scope}${query}`
}

/** Apply the active tag filter, search query and pagination window to the
 * card grid.
 *
 * Reads (dataset/classList state) happen up front, then all class writes are
 * batched into a single pass — interleaving reads and writes would force a
 * layout per card (see the DOM manipulation performance playbook).
 *
 * @param {Object} state - Filter state.
 * @param {HTMLElement[]} state.cards - Card wrappers in the grid.
 * @param {string[][]} state.cardWords - Search words per card (same order).
 * @param {string} state.activeTag - Active tag (lowercased) or '' for all.
 * @param {string[]} state.searchTokens - Normalized search query tokens.
 * @param {number} state.visibleCount - How many matching cards to reveal.
 * @param {HTMLElement|null} state.loadMoreWrapper - Load More container.
 * @param {HTMLElement|null} state.empty - "No results" message.
 * @returns {number} Number of cards matching the filters.
 */
const applyFilter = (state) => {
  const {
    cards,
    cardWords,
    activeTag,
    searchTokens,
    visibleCount,
    loadMoreWrapper,
    empty
  } = state

  let matchCount = 0
  const shouldShow = cards.map((card, index) => {
    if (!matchesTag(card.getAttribute(DATA_TAGS_ATTR), activeTag)) {
      return false
    }

    if (!matchesSearch(searchTokens, cardWords[index])) return false

    matchCount += 1
    return matchCount <= visibleCount
  })

  cards.forEach((card, index) => {
    card.classList.toggle(CLASS_HIDDEN, !shouldShow[index])
  })

  if (loadMoreWrapper) {
    loadMoreWrapper.classList.toggle(CLASS_HIDDEN, matchCount <= visibleCount)
  }

  if (empty) {
    empty.classList.toggle(CLASS_HIDDEN, matchCount > 0)
  }

  return matchCount
}

// I

/** Wire up one section root: tab clicks, search, Load More and live
 * announcements.
 * Safe to call twice on the same root.
 *
 * @param {HTMLElement} root - The section root ([data-blog-filter-root]).
 */
const initBlogFilterNav = (root) => {
  if (root.dataset[DATA_INIT_FLAG]) return
  root.dataset[DATA_INIT_FLAG] = 'true'

  const nav = root.querySelector(SELECTOR_NAV)
  if (!nav) return

  const tabs = Array.from(nav.querySelectorAll(SELECTOR_TAB))
  const cards = Array.from(root.querySelectorAll(SELECTOR_CARD))
  const loadMoreWrapper = root.querySelector(SELECTOR_LOAD_MORE_WRAPPER)
  const loadMoreButton = root.querySelector(SELECTOR_LOAD_MORE)
  const announce = root.querySelector(SELECTOR_ANNOUNCE)
  const empty = root.querySelector(SELECTOR_EMPTY)
  const tabsList = nav.querySelector(SELECTOR_TABS_LIST)
  const searchActivate = nav.querySelector(SELECTOR_SEARCH_ACTIVATE)
  const searchField = nav.querySelector(SELECTOR_SEARCH_FIELD)
  const searchInput = nav.querySelector(SELECTOR_SEARCH_INPUT)
  const searchClose = nav.querySelector(SELECTOR_SEARCH_CLOSE)
  const searchClear = nav.querySelector(SELECTOR_SEARCH_CLEAR)

  const batchSize =
    Number.parseInt(root.getAttribute(DATA_BATCH_SIZE_ATTR), 10) ||
    DEFAULT_BATCH_SIZE

  const state = {
    cards,
    // Tokenized once up front; the search runs on every keystroke.
    cardWords: cards.map((card) =>
      tokenize(card.getAttribute(DATA_SEARCH_TEXT_ATTR))
    ),
    activeTag: ALL_TAG,
    activeLabel: '',
    searchQuery: '',
    searchTokens: [],
    visibleCount: batchSize,
    loadMoreWrapper,
    announce,
    empty
  }

  let announceTimer = null

  const render = ({ debounceAnnounce = false } = {}) => {
    const matchCount = applyFilter(state)

    window.clearTimeout(announceTimer)
    if (debounceAnnounce) {
      announceTimer = window.setTimeout(
        () => announceResults(state, matchCount),
        SEARCH_ANNOUNCE_DELAY_MS
      )
    } else {
      announceResults(state, matchCount)
    }
  }

  const setActiveTab = (tab) => {
    tabs.forEach((other) => {
      const isActive = other === tab
      other.classList.toggle(CLASS_IS_ACTIVE, isActive)
      other.setAttribute('aria-pressed', String(isActive))
    })

    state.activeTag = tab.getAttribute(DATA_TAB_ATTR) || ALL_TAG
    state.activeLabel =
      state.activeTag === ALL_TAG ? '' : tab.textContent.trim()
    state.visibleCount = batchSize

    render()
  }

  const setSearchQuery = (query) => {
    state.searchQuery = query.trim()
    state.searchTokens = tokenize(query)
    state.visibleCount = batchSize
  }

  const toggleClearButton = () => {
    if (searchClear) {
      searchClear.classList.toggle(CLASS_HIDDEN, searchInput.value === '')
    }
  }

  const openSearch = () => {
    // The field hides the tabs, so search always spans every category.
    if (state.activeTag !== ALL_TAG) setActiveTab(tabs[0])

    searchField.classList.remove(CLASS_HIDDEN)
    searchActivate.setAttribute('aria-expanded', 'true')
    // Covered by the field: keep the tabs and toggle out of the tab order.
    if (tabsList) tabsList.inert = true
    searchActivate.inert = true
    searchInput.focus()
  }

  const closeSearch = () => {
    searchField.classList.add(CLASS_HIDDEN)
    searchActivate.setAttribute('aria-expanded', 'false')
    if (tabsList) tabsList.inert = false
    searchActivate.inert = false

    const hadQuery = state.searchTokens.length > 0
    searchInput.value = ''
    toggleClearButton()
    setSearchQuery('')
    if (hadQuery) render()

    searchActivate.focus()
  }

  // One delegated listener on the nav covers every tab (registration is
  // O(1); dispatch cost is at parity with per-tab listeners).
  nav.addEventListener('click', (event) => {
    const tab = event.target.closest(SELECTOR_TAB)
    if (tab && nav.contains(tab)) setActiveTab(tab)
  })

  if (searchActivate && searchField && searchInput && searchClose) {
    searchActivate.addEventListener('click', openSearch)
    searchClose.addEventListener('click', closeSearch)

    searchInput.addEventListener('input', () => {
      toggleClearButton()
      setSearchQuery(searchInput.value)
      render({ debounceAnnounce: true })
    })

    if (searchClear) {
      searchClear.addEventListener('click', () => {
        searchInput.value = ''
        toggleClearButton()
        setSearchQuery('')
        render()
        // The clear button just hid itself, so keep focus in the field.
        searchInput.focus()
      })
    }

    searchField.addEventListener('keydown', (event) => {
      if (event.key === KEY_ESCAPE) closeSearch()
    })
  }

  if (loadMoreButton) {
    loadMoreButton.addEventListener('click', () => {
      state.visibleCount += batchSize
      render()
    })
  }

  // Normalize initial state (Load More visibility, hidden overflow cards).
  applyFilter(state)
}

// M

const initAll = () => {
  document.querySelectorAll(SELECTOR_ROOT).forEach(initBlogFilterNav)
}

if (document.readyState === 'loading') {
  document.addEventListener(EVENT_DOM_CONTENT_LOADED, initAll)
} else {
  initAll()
}

// Theme editor: re-init when this section is re-rendered.
document.addEventListener(EVENT_SHOPIFY_SECTION_LOAD, (event) => {
  const root = event.target.querySelector(SELECTOR_ROOT)
  if (root) initBlogFilterNav(root)
})
